import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import WhitecapColors from "@WhiteCap/Utils/WhiteCapColor";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
	heading = "heading",
	links = "links",
	customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
	fields: {
		[fields.heading]: string;
		[fields.links]: LinkModel[];
		[fields.customCSS]: string;
	};
	extendedStyles?: SubCategoryLinkListStyles;
}

export interface SubCategoryLinkListStyles {
	iconImage?: InjectableCss;
	subCategoryLinkListWrapper?: InjectableCss;
	link?: LinkPresentationProps;
	linkWrapper?: InjectableCss;
	sectionHeading?: InjectableCss;
}

export const subCategoryLinkListStyles: SubCategoryLinkListStyles = {
	iconImage: {
		css: css`
			max-width: 60px;
		`,
	},
	linkWrapper: {
		css: css`
			padding-bottom: 18px;
			align-items: center;
		`,
	},
	link: {
		css: css`
			span {
				color: ${WhitecapColors.orangeLink} !important;
				font-size: 14px;
				line-height: 22px;
				&:hover {
					color: ${getColor("text.main")} !important;
					text-decoration: underline;
				}
			}
		`,
	},
	sectionHeading: {
		css: css`
			margin-bottom: 15px;
			font-size: 14px;
			line-height: 22px;
		`,
	},
};

const defaultCustomCss = `.section-heading {
}

.list-wrapper {
}

.list-link {
}
`;

export const SubCategoryLinkList: React.FC<OwnProps> = ({ fields, extendedStyles }) => {
	const links = useGetLinks(fields.links, (o) => o.fields.destination);

	const styles = useMergeStyles("headerLinkList", subCategoryLinkListStyles, extendedStyles);

	if (links.links.length < 1) {
		return null;
	}

	const customCssWrapper = {
		css: css`
            ${fields.customCSS};
        `,
	};

	return (
		<StyledWrapper {...customCssWrapper}>
			<Typography as="p" {...styles.sectionHeading} className="section-heading">{fields.heading}</Typography>
			<GridContainer className="list-wrapper" {...styles.subCategoryLinkListWrapper}>
				{links.links.map((link, index) => {
					const linkFields = fields.links[index].fields as any;
					return (
						link.url && (
							<GridItem width={[12, 12, 12, 12, 3]} key={`${link.url}.${link.title}`} {...styles.linkWrapper}>
								{linkFields.image && (
									<LazyImage {...styles.iconImage} src={linkFields.image} />
								)}
								<Link
									className="list-link"
									href={link.url}
									target={linkFields.openInNewWindow ? "_blank" : ""}
									{...styles.link}
								>
									{linkFields.categoryTitle ? linkFields.categoryTitle : link.title}
								</Link>
							</GridItem>
						)
					);
				})}
			</GridContainer>
		</StyledWrapper>
	);
};

const basicTab = {
	displayName: "Basic",
	sortOrder: 0,
};

const advancedTab = {
	displayName: "Advanced",
	sortOrder: 1,
};

const widgetModule: WidgetModule = {
	component: SubCategoryLinkList,
	definition: {
		group: "Basic",
		displayName: "Sub Category Link List",
		fieldDefinitions: [
			{
				name: fields.heading,
				displayName: "Heading",
				editorTemplate: "TextField",
				defaultValue: "",
				fieldType: "General",
				tab: basicTab,
			},
			{
				name: fields.links,
				editorTemplate: "ListField",
				getDisplay: (item: HasFields) => {
					const { categoryTitle, overriddenTitle, destination } = item.fields;
					return categoryTitle || overriddenTitle || destination?.value || "";
				},
				defaultValue: [],
				fieldType: "General",
				fieldDefinitions: [
					{
						name: "image",
						displayName: "Category Icon",
						editorTemplate: "ImagePickerField",
						defaultValue: "",
					},
					{
						name: "categoryTitle",
						displayName: "Category Title",
						editorTemplate: "TextField",
						defaultValue: "",
					},
					{
						name: "destination",
						editorTemplate: "LinkField",
						defaultValue: {
							value: "",
							type: "Page",
						},
						isRequired: true,
					},
					{
						name: "openInNewWindow",
						editorTemplate: "CheckboxField",
						defaultValue: false,
					},
				],
				tab: basicTab,
			},
			{
				name: fields.customCSS,
				displayName: "Custom CSS",
				editorTemplate: "CodeField",
				fieldType: "General",
				tab: advancedTab,
				defaultValue: defaultCustomCss,
				isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
				validate: (value) => {
					if (value === undefined || value === null) {
						return "";
					}

					const result = cssLinter.parse(value, { silent: true });
					if (result?.stylesheet?.parsingErrors) {
						// the error output at this time only has room for one line so we just show the first error
						return result.stylesheet.parsingErrors.length <= 0
							? ""
							: result.stylesheet.parsingErrors.map((error) => `${error.reason} on line ${error.line}`)[0];
					}

					return "Unable to parse Css";
				},
				options: {
					mode: "css",
					lint: true,
					autoRefresh: true,
				},
			},
		],
	},
};

export default widgetModule;
